/*eslint-disable*/
import React from "react";
import {
  Column,
  ColumnDef,
  PaginationState,
  flexRender,
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  getFilteredRowModel,
} from "@tanstack/react-table";
import classnames from "classnames";
// A great library for fuzzy filtering/sorting items
import { matchSorter } from "match-sorter";
// react plugin used to create DropdownMenu for selecting items
import Select from "react-select";

import isEmpty from "lodash/isEmpty";

// reactstrap components
import { Container, Row, Col, FormGroup, Input } from "reactstrap";

// Define a default UI for filtering
function DefaultColumnFilter({ column, table }) {
  const { setFilterValue } = column;
  const preFilteredRows = table.getPreFilteredRowModel().rows;
  const count = preFilteredRows.length;

  return (
    <FormGroup>
      <Input
        placeholder={`Search ${count} records...`}
        type="text"
        onChange={(e) => {
          setFilterValue(e.target.value || undefined); // Set undefined to remove the filter entirely
        }}
      />
    </FormGroup>
  );
}

function fuzzyTextFilterFn(row, id, filterValue) {
  return !isEmpty(
    matchSorter([row], filterValue, { keys: [(row) => row.values[id]] })
  );
}

// Let the table remove the filter if the string is empty
fuzzyTextFilterFn.autoRemove = (val) => !val;

// Our table component
function Table({ columns, data, footer }) {
  const [numberOfRows, setNumberOfRows] = React.useState({
    value: 20,
    label: "20 rows",
  });
  const [pageSelect, handlePageSelect] = React.useState({
    value: 0,
    label: "Page 1",
  });
  const filterFns = React.useMemo(
    () => ({
      // Add a new fuzzyTextFilterFn filter type.
      fuzzyText: fuzzyTextFilterFn,
      // Or, override the default text filter to use
      // "startWith"
      text: (row, id, filterValue) => {
        // return rows.filter((row) => {
        const rowValue = row.values[id];
        return rowValue !== undefined
          ? String(rowValue)
              .toLowerCase()
              .startsWith(String(filterValue).toLowerCase())
          : true;
        // });
      },
    }),
    []
  );

  const defaultColumn = React.useMemo(
    () => ({
      // Let's set up our default Filter UI
      Filter: DefaultColumnFilter,
    }),
    []
  );

  const table = useReactTable({
    columns,
    data,
    defaultColumn, // Be sure to pass the defaultColumn option
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    filterFns,
    initialState: {
      pagination: { pageSize: 20, pageIndex: 0 },
    },
  });

  // We don't want to render all of the rows for this example, so cap
  // it for this use case
  // const firstPageRows = rows.slice(0, 10);
  let pageSelectData = Array.apply(null, Array(table.getPageCount())).map(
    function () {}
  );
  let numberOfRowsData = [5, 10, 20, 25, 50, 100];
  return (
    <>
      <div className="ReactTable -striped -highlight primary-pagination">
        <table className="rt-table">
          <thead className="rt-thead -header">
            {table.getHeaderGroups().map((headerGroup, headerGroupIndex) => (
              <tr key={`header-group-${headerGroupIndex}`} className="rt-tr">
                {headerGroup.headers.map((header, key) => (
                  <th
                    key={`header-${header.id}-${key}`}
                    className={classnames("rt-th rt-resizable-header", {
                      "-cursor-pointer":
                        header.column.getCanSort() &&
                        headerGroup.headers.length - 1 !== key,
                      "-sort-asc": header.column.getIsSorted() === "asc",
                      "-sort-desc": header.column.getIsSorted() === "desc",
                    })}
                    colSpan={header.colSpan}
                  >
                    <div
                      className="rt-resizable-header-content"
                      {...{
                        className: header.column.getCanSort()
                          ? "cursor-pointer select-none"
                          : "",
                        onClick: header.column.getToggleSortingHandler(),
                      }}
                    >
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                    </div>
                    {header.column.getCanFilter()
                      ? flexRender(
                          header.column.columnDef.Filter,
                          header.getContext()
                        )
                      : null}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody className="rt-tbody">
            {table.getRowModel().rows.map((row, i) => {
              return (
                <tr
                  key={`row-${row.id}-${i}`}
                  className={classnames(
                    "rt-tr",
                    { " -odd": i % 2 === 0 },
                    { " -even": i % 2 === 1 }
                  )}
                >
                  {row.getVisibleCells().map((cell) => {
                    return (
                      <td key={`cell-${cell.id}`} className="rt-td">
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
        <div className="pagination-bottom">
          <div className="-pagination">
            <div className="-previous" style={{ flex: 0.33 }}>
              <button
                type="button"
                onClick={() => table.previousPage()}
                disabled={!table.getCanPreviousPage()}
                className="-btn"
              >
                Previous
              </button>
            </div>
            <div className="-center">
              <Container>
                <Row className="justify-content-center">
                  <Col md="4" sm="6" xs="12">
                    <Select
                      className="react-select primary"
                      classNamePrefix="react-select"
                      name="pageSelect"
                      value={pageSelect}
                      onChange={(value) => {
                        table.setPageIndex(value.value);
                        handlePageSelect(value);
                      }}
                      options={pageSelectData.map((prop, key) => {
                        return {
                          value: key,
                          label: "Page " + (key + 1),
                        };
                      })}
                      placeholder="Choose Page"
                    />
                  </Col>
                  <Col md="4" sm="6" xs="12">
                    <Select
                      className="react-select primary"
                      classNamePrefix="react-select"
                      name="numberOfRows"
                      value={numberOfRows}
                      onChange={(value) => {
                        table.setPageSize(Number(value.value));
                        setNumberOfRows(value);
                      }}
                      options={numberOfRowsData.map((prop) => {
                        return {
                          value: prop,
                          label: prop + " rows",
                        };
                      })}
                      placeholder="Choose Rows"
                    />
                  </Col>
                </Row>
              </Container>
            </div>
            <div className="-next" style={{ flex: 0.33 }}>
              <button
                type="button"
                onClick={() => table.nextPage()}
                disabled={!table.getCanNextPage()}
                className="-btn"
              >
                Next
              </button>
            </div>
          </div>
        </div>
        {footer && footer({ table })}
      </div>
    </>
  );
}

export default Table;
